import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SiteNav } from '../SiteNav';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { StepProgress } from './StepProgress';
import { Step1ProjectDetails } from './Step1ProjectDetails';
import { Step2FileUpload } from './Step2FileUpload';
import { Step3ReviewPay } from './Step3ReviewPay';
import { calculateQuote, DISCOUNT_CODES } from './quoteCalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initial form state
const initialFormState = {
  clientName: '',
  email: '',
  phone: '',
  projectType: 'refurbishment',
  addressLine1: '',
  addressLine2: '',
  city: '',
  county: '',
  postcode: '',
  gifaM2: 120,
  floors: 1,
  units: 1,
  hasScaledDrawings: false,
  hasPhotos: false,
  infoLevel: 'scaled_drawings',
  drainageChanges: false,
  fullStripOut: false,
  mixedUse: false,
  isListedBuilding: false,
  turnaround: 'standard',
  addWorkmanshipSpec: false,
  addCoverLetter: false,
  addTenderReturnForm: false,
  upsell_contractSupport: false,
  upsell_tenderAnalysis: false,
  upsell_tenderClarifications: false,
  upsell_valueEngineering: false,
  photographFiles: [],
  planFiles: [],
  supportingFiles: [],
  discountCode: ''
};

export const UploadPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(initialFormState);

  // Discount state
  const [discountApplied, setDiscountApplied] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  
  // Terms state
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Calculate quote based on form
  const quote = useMemo(() => calculateQuote(form), [form]);

  // Calculate final amount after discount
  const finalAmount = useMemo(() => {
    if (discountApplied && discountPercent > 0) {
      const discountAmount = quote.totalNow * (discountPercent / 100);
      return Math.max(0, Math.round((quote.totalNow - discountAmount) * 100) / 100);
    }
    return quote.totalNow;
  }, [quote.totalNow, discountApplied, discountPercent]);

  // Discount handlers
  const applyDiscountCode = () => {
    const code = form.discountCode.trim().toUpperCase();
    const discount = DISCOUNT_CODES[code];
    
    if (discount !== undefined) {
      setDiscountApplied(true);
      setDiscountPercent(discount);
      setDiscountError('');
      toast.success(`Discount code applied! ${discount}% discount.`);
    } else if (code === '') {
      setDiscountError('Please enter a discount code.');
    } else {
      setDiscountApplied(false);
      setDiscountPercent(0);
      setDiscountError('Invalid discount code.');
    }
  };

  const removeDiscount = () => {
    setDiscountApplied(false);
    setDiscountPercent(0);
    setForm({ ...form, discountCode: '' });
  };

  // Submit handler
  const handleSubmit = async () => {
    setBusy(true);
    setError(null);

    try {
      if (!quote.hasMinimumInfo) {
        setError('Minimum required: scaled drawings and site photographs.');
        setBusy(false);
        return;
      }

      if (form.photographFiles.length === 0) {
        setError('Please upload at least one photograph of the property.');
        setBusy(false);
        return;
      }

      if (form.planFiles.length === 0) {
        setError('Please upload at least one scaled drawing/plan.');
        setBusy(false);
        return;
      }

      const termsAcceptedAt = new Date().toISOString();
      const intakeRes = await axios.post(`${API}/intake/create`, {
        form: {
          clientName: form.clientName,
          email: form.email,
          phone: form.phone,
          projectType: form.projectType,
          address: {
            line1: form.addressLine1,
            line2: form.addressLine2,
            city: form.city,
            county: form.county,
            postcode: form.postcode,
            fullAddress: [form.addressLine1, form.addressLine2, form.city, form.county, form.postcode].filter(Boolean).join(', ')
          },
          gifaM2: form.gifaM2,
          floors: form.floors,
          units: form.units,
          hasScaledDrawings: form.hasScaledDrawings,
          hasPhotos: form.hasPhotos,
          infoLevel: form.infoLevel,
          drainageChanges: form.drainageChanges,
          fullStripOut: form.fullStripOut,
          mixedUse: form.mixedUse,
          isListedBuilding: form.isListedBuilding,
          turnaround: form.turnaround,
          addWorkmanshipSpec: form.addWorkmanshipSpec,
          addCoverLetter: form.addCoverLetter,
          addTenderReturnForm: form.addTenderReturnForm,
          upsell_contractSupport: form.upsell_contractSupport,
          upsell_tenderAnalysis: form.upsell_tenderAnalysis,
          upsell_tenderClarifications: form.upsell_tenderClarifications,
          upsell_valueEngineering: form.upsell_valueEngineering,
          discountCode: discountApplied ? form.discountCode.trim().toUpperCase() : ''
        },
        quote,
        termsAcceptance: {
          accepted: true,
          acceptedAt: termsAcceptedAt,
          clientEmail: form.email
        }
      });

      const intakeId = intakeRes.data.intakeId;

      // Upload files
      const formData = new FormData();
      formData.append('intakeId', intakeId);
      
      form.photographFiles.forEach((file) => {
        formData.append('files', file);
      });

      form.planFiles.forEach((file) => {
        formData.append('files', file);
      });

      (form.supportingFiles || []).forEach((file) => {
        formData.append('files', file);
      });

      await axios.post(`${API}/intake/upload-files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Handle free submissions or payment
      if (discountApplied && finalAmount === 0) {
        const submitRes = await axios.post(`${API}/intake/submit-free`, null, {
          params: { intakeId }
        });
        
        if (submitRes.data.success) {
          navigate('/success?free=true');
        } else {
          setError('Failed to process free submission. Please try again.');
        }
      } else {
        const checkoutRes = await axios.post(`${API}/payments/create-checkout`, {
          intakeId: intakeId,
          amountGbp: finalAmount,
          email: form.email,
          customerName: form.clientName
        });

        window.location.href = checkoutRes.data.url;
      }

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
      toast.error('Submission failed');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      {/* Navigation */}
      <SiteNav cta="home" />

      {/* Main Content */}
      <main className="pt-28 pb-20">
        <div className="content-container" style={{ maxWidth: '680px' }}>
          {/* Progress Indicator */}
          <StepProgress currentStep={step} />

          {/* Step 1: Project Details */}
          {step === 1 && (
            <Step1ProjectDetails
              form={form}
              setForm={setForm}
              onContinue={() => setStep(2)}
            />
          )}

          {/* Step 2: File Upload */}
          {step === 2 && (
            <Step2FileUpload
              form={form}
              setForm={setForm}
              onBack={() => setStep(1)}
              onContinue={() => setStep(3)}
            />
          )}

          {/* Step 3: Review & Pay */}
          {step === 3 && (
            <Step3ReviewPay
              form={form}
              setForm={setForm}
              quote={quote}
              finalAmount={finalAmount}
              discountApplied={discountApplied}
              discountPercent={discountPercent}
              discountError={discountError}
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
              onApplyDiscount={applyDiscountCode}
              onRemoveDiscount={removeDiscount}
              setDiscountError={setDiscountError}
              onBack={() => setStep(2)}
              onSubmit={handleSubmit}
              busy={busy}
              error={error}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default UploadPage;
